import React, {
    useContext,
    useEffect, useMemo, useRef, useState 
} from 'react';
import {
    Box, Slide, 
    Typography,
    useTheme
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import ProgressBar from '../../components/progressBar';
import {
    TypographyVariants, 
    TypographyWeights
} from '../../../../figmaTheme';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';

type Props = {
    position: number
};

export const PhonePermission = ({
    position
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const [step1Height, setStep1Height] = useState(null);
    const [step2Height, setStep2Height] = useState(null);
    const [step3Height, setStep3Height] = useState(null);
    const [skipAnimation, setSkipAnimation] = useState(false);
    const useStyles = useMemo(() => makeStyles((theme) => ({
        scrollableContainer: {
            overflowY: 'scroll',
            overflowX: 'hidden',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        },
        title: {
            width: '100%',
            marginBottom: '8px',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards 0s`
        },
        subtitle: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards 0s`
        },
        steps: {
            marginTop: '16px',
            width: '100%',
            lineHeight: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: 'fit-content',
            position: 'relative'
        },
        stepsContainer: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '16px'
        },
        step: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            overflow: 'hidden',
            backgroundColor: theme.palette.tertiary.main,
        },
        curtain: {
            position: 'absolute',
            height: '100%',
            backgroundColor: theme.palette.white,
            zIndex: 1
        },
        curtain1Left: {
            width: '41px',
            animation: `$slideLeft ${skipAnimation ? 0 : 0.25}s forwards ${skipAnimation ? 0 : 0.75}s`
        },
        curtain1Right: {
            width: '100%',
            animation: `$slideRight ${skipAnimation ? 0 : 0.75}s forwards ${skipAnimation ? 0 : 0.75}s`
        },
        curtain2Left: {
            width: '30px',
            animation: `$slideLeft ${skipAnimation ? 0 : 0.25}s forwards ${skipAnimation ? 0 : 1.75}s`
        },
        curtain2Right: {
            width: '100%',
            animation: `$slideRight ${skipAnimation ? 0 : 0.75}s forwards ${skipAnimation ? 0 : 1.75}s`
        },
        curtain3Left: {
            width: '30px',
            animation: `$slideLeft ${skipAnimation ? 0 : 0.25}s forwards ${skipAnimation ? 0 : 2.75}s`
        },
        curtain3Right: {
            width: '100%',
            animation: `$slideRight ${skipAnimation ? 0 : 0.75}s forwards ${skipAnimation ? 0 : 2.75}s`
        },
        stepNumber: {
            flexShrink: 0,
            width: '45px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '50% 0 0 50%',
            fontFamily: 'Poppins',
            fontSize: '24px',
            lineHeight: '32px',
            fontWeight: TypographyWeights.BOLD,
            paddingLeft: '8px'
        },
        stepText: {
            display: 'flex',
            flexDirection: 'column',
            padding: '12px',
            backgroundColor: theme.palette.tertiary['75']
        },
        stepTitle: {
            fontWeight: TypographyWeights.BOLD
        },
        svg: {
            width: '100%',
            height: '100%',
            position: 'absolute'
        },
        line: {
            animation: `$snake ${skipAnimation ? 0 : 1.75}s linear forwards ${skipAnimation ? 0 : 1}s`
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: '1' 
            },
            '100%': {
                opacity: '0' 
            },
        },
        '@keyframes snake': {
            '0%': {
                strokeDashoffset: `${step1Height / 2 + step2Height + step3Height / 2 + 32}px` 
            },
            '100%': {
                strokeDashoffset: '0px' 
            },
        },
        '@keyframes slideLeft': {
            '0%': {
                width: '41px' 
            },
            '100%': {
                width: 0 
            },
        },
        '@keyframes slideRight': {
            '0%': {
                width: 'calc(100% - 48px)',
                left: `${Math.max(step1Height, step2Height, step3Height)}px` 
            },
            '100%': {
                width: 0,
                left: '100%' 
            },
        },
    })), [step1Height, step2Height, step3Height, skipAnimation]);
    const classes = useStyles();
    const theme = useTheme();
    const onClick = () => {
        if (!skipAnimation) setSkipAnimation(true);
        else formContext.goNext();
    };

    const onBackClick = () => {
        formContext.goBack();
    };

    const step1 = useRef(null);
    const step2 = useRef(null);
    const step3 = useRef(null);

    useEffect(() => {
        if (!step1.current) return;
        setStep1Height(step1.current.clientHeight);
    }, [step1.current]);

    useEffect(() => {
        if (!step2.current) return;
        setStep2Height(step2.current.clientHeight);
    }, [step2.current]);

    useEffect(() => {
        if (!step3.current) return;
        setStep3Height(step3.current.clientHeight);
    }, [step3.current]);

    useEffect(() => {
        setTimeout(() => {
            setSkipAnimation(true);
        }, 3500);
    });

    return (
        <CardContainer
            sectionKey={'plan'}
            goBack={onBackClick}
            goNext={onClick}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position}
                    sectionKey={'plan'}
                />
                <Box className={classes.scrollableContainer}>
                    <Slide
                        in={true}
                        direction={formContext.screenDirection}
                        timeout={500}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Box className={classes.content}>
                            <Typography
                                variant={TypographyVariants.H3}
                                className={classes.title}
                                dangerouslySetInnerHTML={{
                                    __html: `Next we'll ask for your <span style="font-weight:${TypographyWeights.BOLD};">phone number</span>.`
                                }}
                            />
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.subtitle}
                                dangerouslySetInnerHTML={{
                                    __html: `We know this is sensitive information, and <span style="font-weight:${TypographyWeights.BOLD};">we take your privacy seriously</span>. Here's why we ask:`
                                }}
                            />
                            <Box className={classes.steps}>
                                <svg className={classes.svg}>
                                    <path
                                        className={classes.line}
                                        d={`M ${Math.min(Math.max(step1Height, step2Height, step3Height) / 2 - 4, 58)} ${step1Height / 2} l 0 ${step1Height / 2 + step2Height + step3Height / 2 + 32}`}
                                        fill="none"
                                        strokeDashoffset={`${step1Height / 2 + step2Height + step3Height / 2 + 32}px`}
                                        strokeDasharray={`${step1Height / 2 + step2Height + step3Height / 2 + 32}px`}
                                        stroke={theme.palette.tertiary.main}
                                        strokeWidth={'4px'}
                                    />
                                </svg>
                                <Box className={classes.stepsContainer}>
                                    <Box>
                                        <Box className={`${classes.curtain} ${classes.curtain1Left}`} />
                                        <Box className={`${classes.curtain} ${classes.curtain1Right}`} />
                                        <Box
                                            className={classes.step}
                                            style={{
                                                borderRadius: `${step1Height / 2}px 8px 8px ${step1Height / 2}px` 
                                            }}
                                        >
                                            <Box
                                                className={classes.stepNumber}
                                                style={{
                                                    width: `${Math.max(step1Height, step2Height, step3Height) / 2 - 2}px`,
                                                    maxWidth: '60px',
                                                    borderRadius: `${step1Height / 2}px 0 0 ${step1Height / 2}px` 
                                                }}
                                            >
                                                1
                                            </Box>
                                            <Box
                                                className={classes.stepText}
                                                ref={step1}
                                            >
                                                <Typography
                                                    variant={TypographyVariants.H5}
                                                    className={classes.stepTitle}
                                                >
                                                    {'Proven Effectiveness'}
                                                </Typography>
                                                <Typography
                                                    variant={TypographyVariants.H6}
                                                >
                                                    {'Text messages are a proven tool for changing alcohol habits.'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Box className={`${classes.curtain} ${classes.curtain2Left}`} />
                                        <Box className={`${classes.curtain} ${classes.curtain2Right}`} />
                                        <Box
                                            className={classes.step}
                                            style={{
                                                borderRadius: `${step2Height / 2}px 8px 8px ${step2Height / 2}px` 
                                            }}
                                        >
                                            <Box
                                                className={classes.stepNumber}
                                                style={{
                                                    width: `${Math.max(step1Height, step2Height, step3Height) / 2 - 2}px`,
                                                    maxWidth: '60px',
                                                    borderRadius: `${step2Height / 2}px 0 0 ${step2Height / 2}px` 
                                                }}
                                            >
                                                2
                                            </Box>
                                            <Box
                                                className={classes.stepText}
                                                ref={step2}
                                            >
                                                <Typography
                                                    variant={TypographyVariants.H5}
                                                    className={classes.stepTitle}
                                                >
                                                    {'Simple & Friendly'}
                                                </Typography>
                                                <Typography
                                                    variant={TypographyVariants.H6}
                                                >
                                                    {'Think of us like texting a friend, always just a message away.'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Box className={`${classes.curtain} ${classes.curtain3Left}`} />
                                        <Box className={`${classes.curtain} ${classes.curtain3Right}`} />
                                        <Box
                                            className={classes.step}
                                            style={{
                                                borderRadius: `${step3Height / 2}px 8px 8px ${step3Height / 2}px` 
                                            }}
                                        >
                                            <Box
                                                className={classes.stepNumber}
                                                style={{
                                                    width: `${Math.max(step1Height, step2Height, step3Height) / 2 - 2}px`,
                                                    maxWidth: '60px',
                                                    borderRadius: `${step3Height / 2}px 0 0 ${step3Height / 2}px` 
                                                }}
                                            >
                                                3
                                            </Box>
                                            <Box
                                                className={classes.stepText}
                                                ref={step3}
                                            >
                                                <Typography
                                                    variant={TypographyVariants.H5}
                                                    className={classes.stepTitle}
                                                >
                                                    {'Enhanced Privacy & Security'}
                                                </Typography>
                                                <Typography
                                                    variant={TypographyVariants.H6}
                                                >
                                                    {'We also use phone number for secure log in to protect your data.'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Slide>
                    <Box
                        style={{
                            height: '300px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default PhonePermission;
